import { AgentTaskStatus, CommandType } from '~/protos/agents'
import { isDocumentGeneratingCommand } from '~/task/commandTypeUtils'

/**
 * WHICH TABS SHOW, AND WHEN — single source of truth.
 * =====================================================
 * `TAB_RULES` below is the whole contract: for every tab, it lists the task
 * families it belongs to and the one condition that reveals it. `getVisibleTabs`
 * just walks `TAB_ORDER` and keeps the tabs whose rule passes. Nothing else in
 * the app decides tab visibility — if a tab shows when it shouldn't, the answer
 * is in this file.
 *
 * Two dimensions gate a tab:
 *   1. Task family (`TaskTabProfile`) — "which task is this". Several task types
 *      share the same `AgentTaskStatus` (DataWeave, Code Review and Integration
 *      Test all hit `COMPLETED`; ~14 commands share `DIFF_GENERATED`), so the
 *      family is currently the honest discriminator. (Stage B: once the backend
 *      emits a unique terminal status per family, the family falls out and the
 *      rule can key on status alone.)
 *   2. Content presence (`TabContentFlags`) — whether the backing data exists
 *      yet (a diff, a summary, migration doc entries, …). Each flag is the
 *      runtime echo of a lifecycle status having produced its payload.
 */

export type ViewType =
  | 'summary'
  | 'review-code'
  | 'results'
  | 'agent-progress'
  | 'docs'
  | 'code-generation-summary'

/** The task family a command belongs to — the "which task" axis of a tab rule. */
export enum TaskTabProfile {
  /** Code-producing tasks (coder, code review, simple PR, version upgrade, refactor…). */
  CODE = 'CODE',
  /** Document generators that aren't DataWeave or migration-doc (docs, mapping table, manifests, MUnit, integration test). */
  GENERIC_DOCUMENT = 'GENERIC_DOCUMENT',
  /** DataWeave / sample-data generators. */
  DATAWEAVE = 'DATAWEAVE',
  /** Migration Generator (`MIGRATION_DOCUMENT`). */
  MIGRATION_DOC = 'MIGRATION_DOC',
  /** Migration Accelerator. */
  MIGRATION_ACCELERATOR = 'MIGRATION_ACCELERATOR',
  /** Maven runner (`MVN_CMD`) — has no status of its own; results detected from job metadata. */
  MAVEN = 'MAVEN'
}

/**
 * Whether each tab's backing content exists yet. Each flag is the runtime echo
 * of a lifecycle status having produced its payload.
 */
export interface TabContentFlags {
  /** A reviewable diff payload exists (latest status in `DIFF_BEARING_STATUSES`). */
  hasDiffs: boolean
  /** A summary / review brief exists (deltaContent or reviewBrief present). */
  hasSummaryContent: boolean
  /** Maven produced a summary or raw output. */
  hasMavenResultContent: boolean
  /** Raw deltaContent is non-empty (the single-blob document fallback). */
  hasDeltaContent: boolean
  /** Per-endpoint migration doc entries exist. */
  hasMigrationDocEntries: boolean
  /** Per-endpoint migration risk entries exist. */
  hasMigrationRiskEntries: boolean
  /** A migration code-generation summary exists. */
  hasMigrationCodeGenSummary: boolean
}

/** Left-to-right render order. Every rule below is filtered against this. */
const TAB_ORDER: ViewType[] = [
  'agent-progress',
  'review-code',
  'summary',
  'results',
  'docs',
  'code-generation-summary'
]

/**
 * One boolean per *reason* a tab can appear. Derived once from raw content by
 * `deriveTabSignals` so the table below stays a flat list with no boolean logic.
 * Each signal already accounts for the task family and context that produced it.
 */
export interface TabSignals {
  /** The progress view is enabled. */
  agentProgressEnabled: boolean
  /** A reviewable diff payload exists. */
  hasDiffs: boolean
  /** Summary content exists AND is allowed for this family/context. */
  summaryAvailable: boolean
  /** Maven produced a result artifact. */
  hasMavenResult: boolean
  /** DataWeave produced its result artifact. */
  hasDataweaveResult: boolean
  /** LEGACY: a migration-doc task has only the single-blob doc (no per-endpoint entries). */
  hasMigrationResultBlob: boolean
  /** Per-endpoint migration doc entries exist. */
  hasMigrationDocEntries: boolean
  /** Per-endpoint migration risk entries exist. */
  hasMigrationRiskEntries: boolean
  /** A migration code-generation summary exists. */
  hasMigrationCodeGenSummary: boolean
}

interface TabRule {
  /** Task families this tab can appear for. */
  profiles: TaskTabProfile[]
  /** Tab shows when its family matches AND any one of these signals is true. */
  showWhenAny: (keyof TabSignals)[]
}

const ALL_PROFILES = Object.values(TaskTabProfile)

/**
 * THE TABLE — the entire tab-visibility contract. Read each entry as:
 * "this tab shows for these task families, when ANY of these signals is true".
 * No conditional logic here on purpose; the signals carry the meaning and are
 * derived in `deriveTabSignals`.
 */
const TAB_RULES: Record<ViewType, TabRule> = {
  'agent-progress': { profiles: ALL_PROFILES, showWhenAny: ['agentProgressEnabled'] },
  'review-code': {
    profiles: [TaskTabProfile.CODE, TaskTabProfile.MIGRATION_ACCELERATOR],
    showWhenAny: ['hasDiffs']
  },
  summary: {
    profiles: [
      TaskTabProfile.CODE,
      TaskTabProfile.GENERIC_DOCUMENT,
      TaskTabProfile.MIGRATION_ACCELERATOR
    ],
    showWhenAny: ['summaryAvailable']
  },
  results: {
    profiles: [
      TaskTabProfile.MAVEN,
      TaskTabProfile.DATAWEAVE,
      TaskTabProfile.GENERIC_DOCUMENT,
      TaskTabProfile.MIGRATION_DOC
    ],
    showWhenAny: ['hasDiffs', 'hasMavenResult', 'hasDataweaveResult', 'hasMigrationResultBlob']
  },
  docs: {
    profiles: [TaskTabProfile.MIGRATION_DOC, TaskTabProfile.MIGRATION_ACCELERATOR],
    showWhenAny: ['hasMigrationDocEntries', 'hasMigrationRiskEntries']
  },
  'code-generation-summary': {
    profiles: [TaskTabProfile.MIGRATION_ACCELERATOR],
    showWhenAny: ['hasMigrationCodeGenSummary']
  }
}

/**
 * Turns raw content + context into one boolean per tab-visibility reason. This
 * is the ONE place that still encodes family-specific quirks; each is named and
 * flagged. Stage B (unique status per task) deletes the family scoping here.
 */
export function deriveTabSignals(
  profile: TaskTabProfile,
  flags: TabContentFlags,
  showAgentProgress: boolean
): TabSignals {
  return {
    agentProgressEnabled: showAgentProgress,
    hasDiffs: flags.hasDiffs,
    // Generic-document tasks only surface a summary tab under the progress view.
    summaryAvailable:
      flags.hasSummaryContent && (profile !== TaskTabProfile.GENERIC_DOCUMENT || showAgentProgress),
    hasMavenResult: profile === TaskTabProfile.MAVEN && flags.hasMavenResultContent,
    hasDataweaveResult: profile === TaskTabProfile.DATAWEAVE && flags.hasDeltaContent,
    // Legacy migration docs without per-endpoint entries render their single blob
    // in the results tab instead of the docs tab.
    hasMigrationResultBlob:
      profile === TaskTabProfile.MIGRATION_DOC &&
      !flags.hasMigrationDocEntries &&
      flags.hasDeltaContent,
    hasMigrationDocEntries: flags.hasMigrationDocEntries,
    hasMigrationRiskEntries: flags.hasMigrationRiskEntries,
    hasMigrationCodeGenSummary: flags.hasMigrationCodeGenSummary
  }
}

/** The visible tabs, in display order, for a task. The only entry point. */
export function getVisibleTabs(
  profile: TaskTabProfile,
  flags: TabContentFlags,
  showAgentProgress: boolean
): ViewType[] {
  const signals = deriveTabSignals(profile, flags, showAgentProgress)
  return TAB_ORDER.filter(tab => {
    const rule = TAB_RULES[tab]
    return rule.profiles.includes(profile) && rule.showWhenAny.some(signal => signals[signal])
  })
}

// ─── Command → task family ───────────────────────────────────────────────────

/** DataWeave / sample-data generators. */
export const DATAWEAVE_COMMANDS: ReadonlySet<CommandType> = new Set([
  CommandType.GENERATE_DATAWEAVE,
  CommandType.GENERATE_DATAWEAVE_REPO_AWARE,
  CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION,
  CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION_REPO_AWARE,
  CommandType.DATAWEAVE_TO_DATA_CREATION,
  CommandType.DATAWEAVE_TO_DATA_CREATION_REPO_AWARE
])

/**
 * Maps a command to its display family. Precedence mirrors the historical inline
 * checks: maven → accelerator → migration-doc → (document split into dataweave
 * vs generic) → code.
 */
export function getTaskTabProfile(commandType: CommandType): TaskTabProfile {
  if (commandType === CommandType.MVN_CMD) return TaskTabProfile.MAVEN
  if (commandType === CommandType.MIGRATION_ACCELERATOR) return TaskTabProfile.MIGRATION_ACCELERATOR
  if (commandType === CommandType.MIGRATION_DOCUMENT) return TaskTabProfile.MIGRATION_DOC
  if (isDocumentGeneratingCommand(commandType)) {
    return DATAWEAVE_COMMANDS.has(commandType)
      ? TaskTabProfile.DATAWEAVE
      : TaskTabProfile.GENERIC_DOCUMENT
  }
  return TaskTabProfile.CODE
}

// ─── Diff-bearing status taxonomy (consumed by tuple diff extraction) ──────────

/** Migration statuses whose diffs live under `migrationDocOutput.diffs`. */
export const MIGRATION_DIFF_STATUSES: ReadonlySet<AgentTaskStatus> = new Set([
  AgentTaskStatus.MIGRATION_DOCUMENT_GENERATED,
  AgentTaskStatus.MIGRATION_API_SPEC_GENERATED,
  AgentTaskStatus.MIGRATION_CODE_GENERATED,
  AgentTaskStatus.MIGRATION_REFACTOR_ACCELERATOR_DIFF_GENERATED
])

/** Any status that carries a reviewable diff payload (backs `hasDiffs`). */
export const DIFF_BEARING_STATUSES: ReadonlySet<AgentTaskStatus> = new Set([
  AgentTaskStatus.DIFF_GENERATED,
  AgentTaskStatus.DRAFT_GENERATED,
  ...MIGRATION_DIFF_STATUSES
])
